import { useCallback, useEffect, useState } from 'react';
import { notificationsApi } from './api';

/* Manages the admin's Web Push subscription lifecycle: permission prompt,
   PushManager subscribe/unsubscribe, and syncing the subscription with the
   backend. The service worker (public/sw.js) shows the actual notifications.

   The VAPID public key is fetched from the backend (GET /v1/admin/push/config)
   so the browser subscribes with the exact key the backend signs with — no
   silent key mismatch. There is no build-time fallback — see the comment
   further down for why baking one in is worse than having none.

   State:
     unsupported — browser lacks SW/Push, or the backend has push disabled.
     denied      — the user blocked notifications at the OS/browser level.
     off / on    — subscription is currently inactive / active.
     busy        — a permission prompt or network call is in flight. */

export type PushState = 'unsupported' | 'denied' | 'off' | 'on' | 'busy';

/* There is deliberately NO build-time fallback key. One bundle is deployed to both
   environments, but each backend holds its own VAPID pair (dev and prod differ — verified
   2026-08-11), so any key baked in here is guaranteed wrong for at least one of them. A
   subscription created with the wrong applicationServerKey is accepted by the browser and
   then silently never delivers: the dispatcher sees an enabled bell and no notifications.
   Failing closed — hiding the bell when the backend can't be reached — is the honest
   outcome, and it recovers on its own as soon as /v1/admin/push/config answers again. */

/** Whether the browser has the APIs required for Web Push, independent of any
    key. Exported for unit tests. */
export function browserSupportsPush(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** Convert a base64url VAPID key into the byte array PushManager expects.
    Exported for unit tests. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    bytes[i] = raw.charCodeAt(i);
  }
  return bytes;
}

export function usePushNotifications(): {
  state: PushState;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
} {
  const [state, setState] = useState<PushState>('busy');
  // Resolved at runtime from the backend; null until known (or if disabled).
  const [vapidKey, setVapidKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!browserSupportsPush()) {
        setState('unsupported');
        return;
      }
      // Ask the backend for its VAPID key — it is the only authority on which key this
      // environment signs with. Push disabled server-side, or the endpoint unreachable,
      // both mean "no key we can trust": the bell stays hidden rather than offering a
      // subscription that would never deliver.
      let key: string | null = null;
      try {
        const config = await notificationsApi.config();
        key = config.enabled ? config.vapid_public_key : null;
      } catch {
        key = null;
      }
      if (cancelled) return;
      if (!key) {
        setState('unsupported');
        return;
      }
      setVapidKey(key);
      if (Notification.permission === 'denied') {
        setState('denied');
        return;
      }
      // Guard the SW/subscription query: a rejection here must not leave the
      // hook stuck at the initial 'busy' (which renders the bell as a
      // permanently-disabled spinner). Default to 'off' — the toggle stays
      // usable and `enable()` self-registers the worker.
      let subscription: PushSubscription | null = null;
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        subscription = registration ? await registration.pushManager.getSubscription() : null;
      } catch {
        subscription = null;
      }
      if (cancelled) return;
      setState(subscription ? 'on' : 'off');
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const enable = useCallback(async () => {
    if (!browserSupportsPush() || !vapidKey) return;
    setState('busy');
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState(permission === 'denied' ? 'denied' : 'off');
        return;
      }
      // Ensure the worker is registered (it auto-registers in prod, but a
      // user may enable from a dev/preview session before that runs).
      const registration = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      const json = subscription.toJSON();
      await notificationsApi.subscribe({
        endpoint: json.endpoint ?? '',
        p256dh: json.keys?.p256dh ?? '',
        auth: json.keys?.auth ?? '',
      });
      setState('on');
    } catch {
      setState('off');
    }
  }, [vapidKey]);

  const disable = useCallback(async () => {
    setState('busy');
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = registration ? await registration.pushManager.getSubscription() : null;
      if (subscription) {
        await notificationsApi.unsubscribe(subscription.endpoint).catch(() => undefined);
        await subscription.unsubscribe();
      }
      setState('off');
    } catch {
      setState('off');
    }
  }, []);

  return { state, enable, disable };
}
